import { isAxiosError } from 'axios'
import { Edit, Plus, ReceiptText, RefreshCw, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  expenseService,
  type ExpenseConcept,
  type ExpenseConceptPayload,
  type ExpenseItem,
  type ExpensePaginator,
  type ExpensePayload,
} from '../../services/expense.service'
import { ConfirmationModal } from '../ui/ConfirmationModal'
import { GlobalFilterBar } from '../ui/GlobalFilterBar'
import { Button } from '../ui/button'
import { GlobalPaginator } from '../ui/global-paginator'
import { TableSkeleton } from '../ui/table-skeleton'
import { TabsContent } from '../ui/tabs'

import {
  ExpenseConceptsModal,
  ExpenseFormModal,
} from './modals/ExpenseFormModal'

const itemsPerPage = 10

function emptyPaginator(page = 1): ExpensePaginator {
  return {
    current_page: page,
    data: [],
    from: null,
    to: null,
    per_page: itemsPerPage,
    total: 0,
  }
}

function getErrorMessage(error: unknown, fallback: string) {
  if (isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message || fallback
  }

  return error instanceof Error && error.message ? error.message : fallback
}

function formatExpenseDate(date: string) {
  const [year, month, day] = date.slice(0, 10).split('-')

  return year && month && day ? `${day}/${month}/${year}` : date
}

function formatExpenseAmount(amount: string | number) {
  const parsed = Number(amount)

  if (!Number.isFinite(parsed)) return String(amount)

  return new Intl.NumberFormat('es-MX', {
    maximumFractionDigits: 20,
  }).format(parsed)
}

interface ExpenseTableProps {
  result: ExpensePaginator
  isLoading: boolean
  isError: boolean
  hasFilters: boolean
  onClearFilters: () => void
  onRetry: () => void
  onPageChange: (page: number) => void
  onEdit: (expense: ExpenseItem) => void
  onDelete: (expense: ExpenseItem) => void
}

function ExpenseTable({
  result,
  isLoading,
  isError,
  hasFilters,
  onClearFilters,
  onRetry,
  onPageChange,
  onEdit,
  onDelete,
}: ExpenseTableProps) {
  if (isLoading) {
    return (
      <TableSkeleton
        headers={['Concepto', 'Fecha', 'Cantidad', 'Acciones']}
        columns={['text', 'text', 'text', 'actions']}
        rows={5}
      />
    )
  }

  if (isError) {
    return (
      <div className="flex min-h-56 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <ReceiptText className="mb-3 h-10 w-10 text-red-300" />
        <p className="font-medium text-red-800">No fue posible cargar los gastos</p>
        <Button type="button" variant="outline" className="mt-4" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Reintentar
        </Button>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-table-border bg-table-bg">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <caption className="sr-only">Gastos registrados</caption>
          <thead className="border-b border-table-border bg-table-header-bg text-left text-xs uppercase text-table-header-foreground">
            <tr>
              <th scope="col" className="px-5 py-3 font-medium">Concepto</th>
              <th scope="col" className="px-5 py-3 font-medium">Fecha</th>
              <th scope="col" className="px-5 py-3 text-right font-medium">Cantidad</th>
              <th scope="col" className="px-5 py-3 text-right font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-table-border">
            {result.data.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-14 text-center">
                  <ReceiptText className="mx-auto mb-3 h-9 w-9 text-card-description" />
                  <p className="font-medium text-table-foreground">No hay gastos para mostrar</p>
                  <p className="mt-1 text-sm text-card-description">
                    {hasFilters
                      ? 'No se encontraron registros con los filtros seleccionados.'
                      : 'Registra el primer gasto para comenzar.'}
                  </p>
                  {hasFilters && (
                    <Button type="button" variant="outline" className="mt-4" onClick={onClearFilters}>
                      Quitar filtros
                    </Button>
                  )}
                </td>
              </tr>
            ) : result.data.map((expense) => (
              <tr key={expense.id} className="text-table-foreground transition-colors hover:bg-table-row-hover">
                <td className="px-5 py-4 font-medium">{expense.concept.name}</td>
                <td className="whitespace-nowrap px-5 py-4">{formatExpenseDate(expense.expense_date)}</td>
                <td className="px-5 py-4 text-right tabular-nums">{formatExpenseAmount(expense.amount)}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      aria-label={`Editar gasto ${expense.concept.name}`}
                      title="Editar"
                      onClick={() => onEdit(expense)}
                      className="rounded p-2 text-table-foreground transition-colors hover:bg-muted"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Eliminar gasto ${expense.concept.name}`}
                      title="Eliminar"
                      onClick={() => onDelete(expense)}
                      className="rounded p-2 text-red-500 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <GlobalPaginator
        totalItems={result.total}
        itemsPerPage={result.per_page}
        currentPage={result.current_page}
        from={result.from ?? undefined}
        to={result.to ?? undefined}
        onPageChange={onPageChange}
      />
    </div>
  )
}

export function ExpensesTab() {
  const [expenses, setExpenses] = useState<ExpensePaginator>(() => emptyPaginator())
  const [concepts, setConcepts] = useState<ExpenseConcept[]>([])
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [refreshKey, setRefreshKey] = useState(0)
  const [conceptsRefreshKey, setConceptsRefreshKey] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [areConceptsLoading, setAreConceptsLoading] = useState(true)
  const [conceptsError, setConceptsError] = useState(false)

  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [expenseFormVersion, setExpenseFormVersion] = useState(0)
  const [editingExpense, setEditingExpense] = useState<ExpenseItem | null>(null)
  const [expenseToDelete, setExpenseToDelete] = useState<ExpenseItem | null>(null)

  const [showConceptModal, setShowConceptModal] = useState(false)
  const [reopenExpenseModal, setReopenExpenseModal] = useState(false)
  const [conceptToDelete, setConceptToDelete] = useState<ExpenseConcept | null>(null)

  const hasFilters = Boolean(search || dateFrom || dateTo)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setIsError(false)

    expenseService.getExpenses({
      search: search || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      per_page: itemsPerPage,
    }).then((response) => {
      if (!active) return
      setExpenses(response.data)
      if (response.data.current_page !== page) setPage(response.data.current_page)
    }).catch(() => {
      if (!active) return
      setExpenses(emptyPaginator(page))
      // setIsError(true)
    }).finally(() => {
      if (active) setIsLoading(false)
    })

    return () => {
      active = false
    }
  }, [dateFrom, dateTo, page, refreshKey, search])

  useEffect(() => {
    let active = true
    setAreConceptsLoading(true)
    setConceptsError(false)

    expenseService.getConcepts().then((response) => {
      if (active) setConcepts(response.data)
    }).catch(() => {
      if (!active) return
      setConcepts([])
      // setConceptsError(true)
    }).finally(() => {
      if (active) setAreConceptsLoading(false)
    })

    return () => {
      active = false
    }
  }, [conceptsRefreshKey])

  const clearFilters = () => {
    setSearch('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const openCreateExpense = () => {
    setEditingExpense(null)
    setExpenseFormVersion((value) => value + 1)
    setShowExpenseModal(true)
  }

  const openEditExpense = (expense: ExpenseItem) => {
    setEditingExpense(expense)
    setExpenseFormVersion((value) => value + 1)
    setShowExpenseModal(true)
  }

  const closeExpenseModal = () => {
    setShowExpenseModal(false)
    setEditingExpense(null)
  }

  const saveExpense = async (payload: ExpensePayload) => {
    try {
      const response = editingExpense
        ? await expenseService.updateExpense(editingExpense.id, payload)
        : await expenseService.createExpense(payload)
      toast.success(response.message || (editingExpense ? 'Gasto actualizado' : 'Gasto registrado'))
      closeExpenseModal()
      setRefreshKey((value) => value + 1)
    } catch (error) {
      toast.error(getErrorMessage(error, 'No fue posible guardar el gasto'))
    }
  }

  const deleteExpense = async () => {
    if (!expenseToDelete) return

    try {
      const response = await expenseService.deleteExpense(expenseToDelete.id)
      toast.success(response.message || 'Gasto eliminado')
      setExpenseToDelete(null)
      setRefreshKey((value) => value + 1)
    } catch (error) {
      toast.error(getErrorMessage(error, 'No fue posible eliminar el gasto'))
    }
  }

  const openConceptManager = () => {
    setReopenExpenseModal(showExpenseModal)
    setShowExpenseModal(false)
    setShowConceptModal(true)
  }

  const closeConceptManager = () => {
    setShowConceptModal(false)

    if (reopenExpenseModal) {
      setShowExpenseModal(true)
      setReopenExpenseModal(false)
    }
  }

  const createConcept = async (payload: ExpenseConceptPayload) => {
    try {
      const response = await expenseService.createConcept(payload)
      toast.success(response.message || 'Concepto creado')
      setConceptsRefreshKey((value) => value + 1)
      return true
    } catch (error) {
      toast.error(getErrorMessage(error, 'No fue posible guardar el concepto'))
      return false
    }
  }

  const updateConcept = async (id: number, payload: ExpenseConceptPayload) => {
    try {
      const response = await expenseService.updateConcept(id, payload)
      toast.success(response.message || 'Concepto actualizado')
      setConceptsRefreshKey((value) => value + 1)
      return true
    } catch (error) {
      toast.error(getErrorMessage(error, 'No fue posible guardar el concepto'))
      return false
    }
  }

  const requestConceptDeletion = (concept: ExpenseConcept) => {
    setConceptToDelete(concept)
    setShowConceptModal(false)
  }

  const closeConceptDeletion = () => {
    setConceptToDelete(null)
    setShowConceptModal(true)
  }

  const deleteConcept = async () => {
    if (!conceptToDelete) return

    try {
      const response = await expenseService.deleteConcept(conceptToDelete.id)
      toast.success(response.message || 'Concepto eliminado')
      setConceptToDelete(null)
      setShowConceptModal(true)
      setConceptsRefreshKey((value) => value + 1)
    } catch (error) {
      toast.error(getErrorMessage(error, 'No fue posible eliminar el concepto'))
      setShowConceptModal(true)
      setConceptToDelete(null)
    }
  }

  return (
    <TabsContent value="expenses" className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-header-foreground">Gestión de gastos</h2>
          <p className="mt-1 text-sm text-card-description">
            Registra y consulta los gastos operativos de la farmacia.
          </p>
        </div>
        <Button type="button" onClick={openCreateExpense} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Nuevo gasto
        </Button>
      </div>

      <GlobalFilterBar
        show={['search', 'dates']}
        values={{ search, startDate: dateFrom, endDate: dateTo }}
        onChange={(changes) => {
          if (changes.search !== undefined) setSearch(changes.search)
          if (changes.startDate !== undefined) setDateFrom(changes.startDate)
          if (changes.endDate !== undefined) setDateTo(changes.endDate)
          setPage(1)
        }}
        customOptions={{ searchPlaceholder: 'Buscar por concepto...' }}
        hasActiveFilters={hasFilters}
        onClearFilters={clearFilters}
      />

      <ExpenseTable
        result={expenses}
        isLoading={isLoading}
        isError={isError}
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        onRetry={() => setRefreshKey((value) => value + 1)}
        onPageChange={setPage}
        onEdit={openEditExpense}
        onDelete={setExpenseToDelete}
      />

      <ExpenseFormModal
        open={showExpenseModal}
        formVersion={expenseFormVersion}
        expense={editingExpense}
        concepts={concepts}
        conceptsLoading={areConceptsLoading}
        onOpenChange={(open) => {
          if (!open) closeExpenseModal()
        }}
        onOpenConcepts={openConceptManager}
        onSubmit={saveExpense}
      />

      <ExpenseConceptsModal
        open={showConceptModal}
        concepts={concepts}
        isLoading={areConceptsLoading}
        isError={conceptsError}
        onOpenChange={(open) => {
          if (!open) closeConceptManager()
        }}
        onRetry={() => setConceptsRefreshKey((value) => value + 1)}
        onCreate={createConcept}
        onUpdate={updateConcept}
        onDeleteRequest={requestConceptDeletion}
      />

      <ConfirmationModal
        isOpen={expenseToDelete !== null}
        onClose={() => setExpenseToDelete(null)}
        onConfirm={deleteExpense}
        preset="delete"
        itemName="gasto"
        details={expenseToDelete ? (
          <p className="rounded-md bg-muted px-3 py-2 text-sm text-card-foreground">
            {expenseToDelete.concept.name} · {formatExpenseDate(expenseToDelete.expense_date)}
          </p>
        ) : undefined}
      />

      <ConfirmationModal
        isOpen={conceptToDelete !== null}
        onClose={closeConceptDeletion}
        onConfirm={deleteConcept}
        preset="delete"
        itemName="concepto"
        description="¿Estás seguro de que deseas eliminar este concepto?"
        details={conceptToDelete ? (
          <p className="rounded-md bg-muted px-3 py-2 text-sm font-medium text-card-foreground">
            {conceptToDelete.name}
          </p>
        ) : undefined}
      />
    </TabsContent>
  )
}
